import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, LoaderPulse } from '@reservamos/elements';
import { trackEvent } from 'user-analytics';
import { WALLET_ADD_BUTTON_PRESSED } from 'constants/TrackEvents';
import { isIos } from 'utils/Helpers';
import { downloadPkpass } from 'utils/ticket-wallet';
import Header from 'components/Header';
import BrandLogo from 'components/BrandLogo';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import errorMonitoring from '../errorMonitoring';
import { setError as setErrorAction } from '../actions';

/**
 * TicketWalletDownload component.
 * Handles the download of pkpass files for wallet integration.
 * Users are redirected here from email links to download their tickets to their mobile wallet.
 *
 * @component
 * @returns {JSX.Element} The rendered TicketWalletDownload component
 */
const TicketWalletDownload = () => {
  const { purchaseToken, ticketId } = useParams();
  const { t } = useTranslation('purchase');
  const history = useHistory();
  const dispatch = useDispatch();
  const env = useWhitelabelEnvs();

  const isiOS = isIos();

  useEffect(() => {
    let redirectTimeout;

    const handleDownload = async () => {
      try {
        const apiKey = env.api?.mobileKey;
        const platform = isiOS ? 'ios' : 'android';

        await downloadPkpass(purchaseToken, ticketId, apiKey, env.api?.purchaseUrl, platform);

        // Track the wallet download event
        trackEvent(WALLET_ADD_BUTTON_PRESSED, {
          'Download Source': 'email',
          'Platform': platform,
          ticketId,
          'Purchase Token': purchaseToken,
        });

        // Redirect to purchase complete page
        redirectTimeout = setTimeout(() => {
          history.push(`/purchase/${purchaseToken}/complete`);
        }, 2000);
      } catch (err) {
        // Notify Sentry about the wallet download failure
        errorMonitoring.notify({
          error: err,
          info: {
            from: 'TicketWalletDownload.handleDownload',
            ticketId,
            purchaseToken,
            platform: isiOS ? 'ios' : 'android',
            downloadSource: 'email',
          },
        });

        // Show error alert to user
        dispatch(setErrorAction(400, 'ticket_wallet_download_failed', 'error', false));

        // Redirect to purchase complete page
        redirectTimeout = setTimeout(() => {
          history.push(`/purchase/${purchaseToken}/complete`);
        }, 2000);
      }
    };

    handleDownload();

    return () => {
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [purchaseToken, ticketId, env, history, isiOS, dispatch]);

  let message;
  if (isiOS) {
    message = t('ticket_wallet_download.ios_message');
  } else {
    message = t('ticket_wallet_download.android_message');
  }

  return (
    <div className="l-complete">
      <Header isFixed>
        <BrandLogo />
      </Header>
      <Spacing vertical size="XXL" alignItems="center">
        <LoaderPulse isAccent size="S" />
        <Text weight="bold" size="L">
          {t('ticket_wallet_download.downloading')}
        </Text>
        <Text color="grayMedium" textAlign="center">
          {message}
        </Text>
      </Spacing>
    </div>
  );
};

export default TicketWalletDownload;
